import type { components } from "@majitap/sdk";
import { formatDateTime, formatMoney, formatPhone } from "@majitap/ui";
import type { ReactNode } from "react";
import { useCompanyFormat } from "../../company";
import { CHANNEL } from "../../labels";
import { RouterLink } from "../../router-link";
import styles from "./record.module.css";

type AdminOrder = components["schemas"]["AdminOrder"];

/** A titled card in the order page's right column. */
export function SideCard({ title, link, children }: { title: string; link?: ReactNode; children: ReactNode }) {
  return (
    <section className={styles.card} aria-label={title}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>{title}</h2>
        {link}
      </div>
      {children}
    </section>
  );
}

/** Label and value pairs; empty values are left out. */
export function Facts({ rows }: { rows: [label: string, value: ReactNode][] }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <dl className={styles.kv}>
      {shown.map(([label, value]) => (
        <div key={label} style={{ display: "contents" }}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

/** Who the order is for and where it goes. */
export function CustomerCard({ order: o }: { order: AdminOrder }) {
  const href = o.business ? `/businesses/${o.business.id}` : `/customers/${o.customer.id}`;
  return (
    <SideCard
      title="Customer"
      link={
        <RouterLink href={href} className={styles.link}>
          Open
        </RouterLink>
      }
    >
      <div className={styles.person}>
        <span className={styles.avatar} aria-hidden>
          {initialsOf(o.customer.name)}
        </span>
        <span>
          <strong>{o.customer.name}</strong>
          <br />
          <span className={styles.small}>{formatPhone(o.customer.phone_number) || "No phone"}</span>
        </span>
      </div>
      <Facts
        rows={[
          ["Billed to", o.business ? `${o.business.name} (business)` : "The customer"],
          ["Deliver to", o.location ? [o.location.label, o.location.address_line].filter(Boolean).join(" · ") : null],
        ]}
      />
    </SideCard>
  );
}

/** When, how and by whom it was placed, and what came with it. */
export function DetailsCard({ order: o }: { order: AdminOrder }) {
  const format = useCompanyFormat();
  return (
    <SideCard title="Details">
      <Facts
        rows={[
          ["Scheduled", o.scheduled_for ? formatDateTime(o.scheduled_for, format) : "As soon as possible"],
          ["LPO", o.lpo_reference],
          ["Channel", CHANNEL[o.channel]],
          ["Placed by", o.placed_by?.name ?? (o.api_token_id ? "An API token" : null)],
          ["Sale counts for", o.sales_staff?.name],
          ["Voucher", o.voucher ? `${o.voucher.code} · ${formatMoney(o.discount_amount, format)} off` : null],
          ["Notes", o.notes],
        ]}
      />
    </SideCard>
  );
}
